import React, {
  useRef,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useMemo,
} from 'react';

export interface VoiceConversationDemoHandle {
  container: HTMLDivElement | null;
  content: HTMLDivElement | null;
  header: HTMLHeadingElement | null;
  paragraph: HTMLParagraphElement | null;
  video: HTMLDivElement | null;
}

export interface VoiceConversationDemoProps {
  isDarkMode: boolean;
  startAnimation: boolean;
}

const VoiceConversationDemo = forwardRef<
  VoiceConversationDemoHandle,
  VoiceConversationDemoProps
>(({ isDarkMode, startAnimation }, ref) => {
  const sectionRef = useRef<HTMLDivElement>(null);

  // Use distinct refs for desktop + mobile headers (having two elements share one ref is ambiguous).
  const desktopHeaderRef = useRef<HTMLHeadingElement>(null);
  const mobileHeaderRef = useRef<HTMLHeadingElement>(null);

  const paragraphRef = useRef<HTMLParagraphElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);

  const hasStartedRef = useRef(false);

  // Compute once; whichever header exists in the DOM for the current breakpoint will be non-null.
  const headerRef = desktopHeaderRef.current ?? mobileHeaderRef.current;

  useImperativeHandle(
    ref,
    () => ({
      container: sectionRef.current,
      content: sectionRef.current,
      header: headerRef,
      paragraph: paragraphRef.current,
      video: videoContainerRef.current,
    }),
    [headerRef]
  );

  const baseUrl = useMemo(
    () =>
      typeof import.meta !== 'undefined' && (import.meta as any).env?.BASE_URL
        ? (import.meta as any).env.BASE_URL
        : '/',
    []
  );

  const webmSrc = `${baseUrl}landing/aleph_interrupt_voice_demo.webm`;
  const mp4Src = `${baseUrl}landing/aleph_interrupt_voice_demo.mp4`;

  useEffect(() => {
    if (!startAnimation) return;
    if (hasStartedRef.current) return;

    const video = videoRef.current;
    if (!video) return;

    video.muted = true;
    video.playsInline = true;
    video.play().catch(() => {});
    hasStartedRef.current = true;
  }, [startAnimation]);

  return (
    <section
      ref={sectionRef}
      className="
        w-full
        h-dvh
        px-8 lg:px-16 xl:px-24
        pt-38 md:pt-42
      "
    >
      {/* Grid defines the entire page height */}
      <div
        className="
          grid
          h-full
          grid-rows-[auto_auto_auto]
          lg:grid-cols-2
          lg:grid-rows-[auto_auto]
          gap-y-18
          lg:gap-y-28
          lg:gap-x-12
        "
      >
        {/* Header desktop */}
        <h2
          ref={desktopHeaderRef}
          className="
            hidden
            lg:block
            col-span-full
            text-left
            text-3xl 2xs:text-4xl xs:text-5xl lg:text-6xl
            font-bold
            main-title portrait-text-app
          "
        >
          Interrupt playback<wbr /> to ask a question
        </h2>

        {/* Header mobile */}
        <h2
          ref={mobileHeaderRef}
          className="
            block
            lg:hidden
            col-span-full
            text-left
            text-3xl 3xs:text-[1.95rem] 2xs:text-4xl xs:text-5xl sm:text-6xl
            font-bold
            main-title portrait-text-app
          "
        >
          Interrupt playback<br /> to ask a question
        </h2>

        {/* Paragraph */}
        <p
          ref={paragraphRef}
          className={`
            text-lg xs:text-xl md:text-2xl lg:text-2xl
            ${isDarkMode ? 'text-theme-secondary-dark' : 'text-blue-700'}
          `}
        >
          Aleph isn&apos;t just a reader, it&apos;s also a smart listener. You can
          interrupt playback on mobile or desktop to share your thoughts or ask a question. Don&apos;t
          worry, Aleph won&apos;t spoil the plot.
        </p>

        {/* Video slot */}
        <div
          ref={videoContainerRef}
          className="
            col-span-full
            lg:col-span-1
            flex
            items-end
            justify-center
            overflow-hidden
          "
        >
          {/* Aspect ratio locked video that cannot exceed its slot */}
          <div className="w-full max-h-full aspect-946/1080">
            <video
              ref={videoRef}
              className="
                bg-transparent
                w-full
                h-full
                object-contain
                object-bottom
                pointer-events-none
                select-none
              "
              loop
              muted
              playsInline
              preload="auto"
              controls={false}
              aria-hidden="true"
            >
              <source src={mp4Src} type='video/mp4; codecs="hvc1"' />
              <source src={webmSrc} type="video/webm" />
            </video>
          </div>
        </div>
      </div>
    </section>
  );
});

VoiceConversationDemo.displayName = 'VoiceConversationDemo';
export default VoiceConversationDemo;
